import { useCallback, useEffect, useState } from 'react';
import {
  LuPill, LuThermometer, LuBandage, LuShieldPlus, LuDroplet, LuCandy, LuWind, LuActivity, LuFlower2,
} from 'react-icons/lu';

/**
 * The essentials of a Swiss home medicine cabinet, shelf by shelf (three per
 * shelf), each with the shop product we suggest when it's missing. Edit this
 * list to change what the cabinet asks about or recommends.
 *
 * `shape` picks the pack drawn on the shelf; `tint` is its accent colour.
 */
export const CABINET_ITEMS = [
  { id: 'pain', pharmacode: '7739206', shape: 'box', tint: 'rgb(var(--brand))', Icon: LuPill },
  { id: 'thermometer', pharmacode: '3106006', shape: 'stick', tint: '#3B82C4', Icon: LuThermometer },
  { id: 'plasters', pharmacode: '6518258', shape: 'box', tint: '#D9822B', Icon: LuBandage },
  { id: 'disinfectant', pharmacode: '2956493', shape: 'spray', tint: '#2F9E77', Icon: LuShieldPlus },
  { id: 'ointment', pharmacode: '7750200', shape: 'tube', tint: '#8B6BC9', Icon: LuDroplet },
  { id: 'throat', pharmacode: '3264389', shape: 'box', tint: '#E0A33A', Icon: LuCandy },
  { id: 'nose', pharmacode: '1755657', shape: 'spray', tint: '#3B9FC4', Icon: LuWind },
  { id: 'stomach', pharmacode: '755307', shape: 'bottle', tint: '#2E8C8C', Icon: LuActivity },
  { id: 'allergy', pharmacode: '7775579', shape: 'box', tint: '#7FA33A', Icon: LuFlower2 },
];

export const STORAGE_KEY = 'medicineCabinet';
const STORAGE_VERSION = 1;
// Both home-page trees (desktop and mobile) mount the cabinet at once; this
// event keeps them in step when one of them changes the ticks.
const CHANGE_EVENT = 'dialine:medicine-cabinet';
const KNOWN = new Set(CABINET_ITEMS.map((i) => i.id));

/** Ids the visitor marked as "at home", in cabinet order. Never throws. */
export function readHave() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (!parsed || parsed.v !== STORAGE_VERSION || !Array.isArray(parsed.have)) return [];
    const have = new Set(parsed.have.filter((id) => KNOWN.has(id)));
    return CABINET_ITEMS.map((i) => i.id).filter((id) => have.has(id));
  } catch {
    return [];
  }
}

export function writeHave(ids) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ v: STORAGE_VERSION, have: ids }));
  } catch {
    // Private mode / storage full: the cabinet still works for this visit.
  }
}

export const toggleHave = (ids, id) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);

/** Which encouragement to show for `n` of `total` filled. */
export function tierFor(n, total) {
  if (n <= 0) return 'empty';
  if (n >= total) return 'full';
  const share = n / total;
  if (share < 0.4) return 'low';
  if (share < 0.8) return 'mid';
  return 'high';
}

/** The visitor's ticks, persisted and shared between mounted cabinets. */
export function useCabinetState() {
  const [have, setHave] = useState(readHave);

  useEffect(() => {
    const sync = () => setHave(readHave());
    window.addEventListener(CHANGE_EVENT, sync);
    return () => window.removeEventListener(CHANGE_EVENT, sync);
  }, []);

  const update = useCallback((next) => {
    writeHave(next);
    setHave(next);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  const toggle = useCallback((id) => update(toggleHave(readHave(), id)), [update]);
  const reset = useCallback(() => update([]), [update]);

  return { have, toggle, reset };
}
